'use strict';

import * as d3 from 'd3';
import { ellipsisText } from './utils';

export const X_AXIS_CLASSNAME = 'x-axis';
export const X_AXIS_LABEL_CLASSNAME = 'x-axis-label';

export const getXScale = (range, domain, options = {}) => {
  const scaleType = options.scaleType !== undefined && d3[options.scaleType] ? options.scaleType : 'scaleLinear';
  const d3Scale = d3[scaleType]();
  const maxDomain = Math.max.apply(Math, domain);
  if (maxDomain === 0) {
    domain = [0];
  }
  const xScale = d3Scale.range(range).domain(domain);
  xScale.scaleType = scaleType;
  if (typeof options.padding === 'number') xScale.padding(options.padding);
  return xScale;
};

export const getXAxis = (xScale, options = {}) => {
  const xAxis = d3.axisBottom(xScale);
  const maxDomainRange = ('' + Math.round(xScale.domain()[1])).length;
  const xScaleRange = xScale.range();
  const totalTicks = ['scaleBand', 'scalePoint'].includes(xScale.scaleType) ? xScale.domain().length : xScale.ticks().length;
  const charWidth = 12;
  const pxInOneTick = 50 + (maxDomainRange * charWidth);
  const decentTicks = Math.round(xScaleRange[1] / pxInOneTick);
  if (totalTicks > decentTicks) {
    if (xScale.scaleType === 'scaleTime') {
      let customTick = [xScale.domain()[0]];
      if (options.isHideBorderTick) {
        customTick = [];
      }
      const divideValue = Math.round(totalTicks / (decentTicks + 1));
      const allTicks = xScale.ticks();
      let loopValue = divideValue;
      allTicks.forEach((tick, index) => {
        if (Math.round(index) === loopValue) {
          customTick.push(tick);
          loopValue = loopValue + divideValue;
        }
      });
      if (!options.isHideBorderTick) {
        customTick.push(xScale.domain()[1]);
      }
      xAxis.tickValues(customTick);
    } else {
      xAxis.ticks(decentTicks);
    }
  }
  if (typeof options.tickSizeInner === 'number') {
    xAxis.tickSizeInner(options.tickSizeInner);
  }
  const tickSizeOuter = options.tickSizeOuter !== undefined ? options.tickSizeInner : 5;
  xAxis.tickSizeOuter(tickSizeOuter);
  const tickPadding = options.tickPadding !== undefined ? options.tickPadding : 10;
  xAxis.tickPadding(tickPadding);
  if (typeof options.tickFormat === 'function') {
    xAxis.tickFormat(val => options.tickFormat(xScale.domain(), val));
  }
  if (options.isHideHorizontalGridLine) {
    xAxis.tickSize(0);
  }
  return xAxis;
};

export const renderXAxis = ($container, xAxis, options = {}) => {
  const _translate = {
    x: options.translate && options.translate.x ? options.translate.x : 0,
    y: options.translate && options.translate.y ? options.translate.y : 0
  };
  const joinedXAxis = $container.selectAll(`.${X_AXIS_CLASSNAME}`).data([0]);
  const xAxisEnter = joinedXAxis
    .enter()
    .append('g')
    .attr('class', X_AXIS_CLASSNAME)
    .attr('transform', `translate(${_translate.x}, ${_translate.y})`)
    .call(xAxis);
  joinedXAxis
    .transition().duration(options.transitionDuration)
    .attr('transform', `translate(${_translate.x}, ${_translate.y})`)
    .call(xAxis);
  joinedXAxis
    .exit()
    .transition().duration(options.transitionDuration)
    .remove();
  const $xAxis = $container.select(`.${X_AXIS_CLASSNAME}`);
  const tickPadding = Number(xAxis.tickPadding());
  const $tickTexts = $xAxis.selectAll('.tick > text');
  const isRotated = typeof options.rotate === 'number';
  if (isRotated) {
    $tickTexts
      .attr('transform', `rotate(${options.rotate})`)
      .attr('text-anchor', 'start');
    if (options.rotate === 90) {
      $tickTexts
        .attr('x', '8')
        .attr('y', '16')
        .attr('dy', '-12');
    }
    if (options.rotate === -90) {
      $tickTexts
        .attr('text-anchor', 'end')
        .attr('x', '-8')
        .attr('y', '16')
        .attr('dy', '-12');
    }
  }
  if (options.ellipsis !== undefined) {
    const width = options.ellipsis.width - tickPadding - xAxis.tickSizeOuter();
    $tickTexts.call(ellipsisText, width);
  }
  if (options.label) {
    const renderAxisLabel = $text => {
      const xAxisBBox = $xAxis.node().getBBox();
      const tickTextBBox = $tickTexts.empty() ? null : $tickTexts.node().getBBox();
      const tickTextY = tickTextBBox === null ? 0 : tickTextBBox.y;
      const tickTextHeight = tickTextBBox === null ? 0 : (isRotated ? tickTextBBox.width : tickTextBBox.height);
      const offsetY = options.label.offsetY ? options.label.offsetY : 0;
      const fontSize = options.label.fontSize || 13;
      $text
        .attr('x', xAxisBBox.width / 2)
        .attr('y', tickTextY + tickTextHeight + xAxis.tickPadding() + offsetY + (fontSize / 2))
        .attr('fill', 'currentColor')
        .style('font-size', fontSize);
      if (options.label.fontStyle) {
        $text.style('font-family', options.label.fontStyle);
      }
      $text.text(options.label.text);
    };
    const _xAxis = xAxisEnter.merge(joinedXAxis);
    const joinedXAxisText = _xAxis.selectAll(`.${X_AXIS_LABEL_CLASSNAME}`).data([0]);
    joinedXAxisText
      .enter()
      .append('text')
      .attr('class', X_AXIS_LABEL_CLASSNAME)
      .call(renderAxisLabel);
    joinedXAxisText
      .transition().duration(options.transitionDuration)
      .call(renderAxisLabel);
    joinedXAxisText
      .transition().duration(options.transitionDuration)
      .remove();
  }
  return $xAxis;
};

const XAxis = {
  X_AXIS_CLASSNAME: X_AXIS_CLASSNAME,
  X_AXIS_LABEL_CLASSNAME: X_AXIS_LABEL_CLASSNAME,
  getXScale: getXScale,
  getXAxis: getXAxis,
  renderXAxis: renderXAxis
};

export default XAxis;
